import '../upsert-collection-layer/upsert-collection-layer.js';
import '../wishlist-collection/wishlist-collection.js';
import {classMap} from 'lit/directives/class-map.js';

import {css, CSSResult, html, LitElement, nothing, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {Messages} from '../../../@types/wishlist.js';
import {consume} from '@lit/context';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {repeat} from 'lit/directives/repeat.js';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import styles from './wishlist_collections.scss';
import {collectionsContext, productsContext} from '../../../scripts/context/wishlistContext.js';
import {CollectionsState, ProductsState} from '../../../scripts/context/wishlist-state.js';
import {trackPageView, trackViewCollections, trackViewWishlist} from '../../../scripts/service/cube-tracking-layer.js';
import {CollectionMenuState} from '../../../scripts/context/menu-state.js';
import {collectionMenuContext} from '../../../scripts/context/menuContext.js';
import checkmarkIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_check.svg';
import {pushToSnackbar} from '../../../scripts/service/snackbar-service.js';
import {DetailsError} from '../wishlist-collection-details-error/wishlist-collection-details-error.js';
import {addTitleToIcon} from '../icon/icon-accessibility.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';

@customElement('ents-wishlist-collections')
export class WishlistCollections extends LitElement {
  @property({type: Boolean, attribute: 'logged-in'})
  private loggedIn: boolean = false;

  @property({type: Boolean, attribute: 'from-no-longer-shared-collection'})
  private fromNoLongerSharedCollection: boolean = false;

  @consume({context: collectionsContext, subscribe: true})
  @property({attribute: false})
  private collectionsState!: CollectionsState;

  @consume({context: productsContext, subscribe: true})
  @property({attribute: false})
  private productsState!: ProductsState;

  @consume({context: collectionMenuContext, subscribe: true})
  @property({attribute: false})
  private collectionMenuState!: CollectionMenuState;

  @consume({context: i18nContext})
  private messages!: Messages;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  connectedCallback() {
    super.connectedCallback();
    setTimeout(() => this.trackPageEvents());
  }

  private trackPageEvents() {
    trackPageView('collection_list', this.collectionsState.entities.length === 0, this.loggedIn);
    trackViewWishlist('collection', this.productsState.entities);
    trackViewCollections('collection', this.collectionsState.entities, this.productsState);
  }

  private openCreateCollectionLayer() {
    this.collectionMenuState.openCreateLayer();
  }

  protected renderNoLongerSharedWishlistHint() {
    if (!this.fromNoLongerSharedCollection) {
      return nothing;
    }
    if (this.loggedIn) {
      pushToSnackbar(`${this.messages['ents.wishlist.collection.details.not.exist.snackbar.text']}`);
      return nothing;
    }
    return html`
      <ents-wishlist-collection-details-error
        .error="${DetailsError.COLLECTION_NOT_EXISTS}"
      >
      </ents-wishlist-collection-details-error>
    `;
  }

  protected renderLoginHint() {
    return html`
      <ents-wishlist-collection-details-error
        .error="${DetailsError.NOT_LOGGED_IN}"
      >
      </ents-wishlist-collection-details-error>
      ${this.renderNoLongerSharedWishlistHint()}
    `;
  }

  render() {
    if (!this.loggedIn) {
      return this.renderLoginHint();
    }
    return html`
      <div class="ents-wishlist-collection-container">
        ${this.renderNoLongerSharedWishlistHint()}
        <div class="ents-wishlist-collection-actions
        ${classMap({
          'ents-wishlist-collection-actions__empty': this.collectionsState.entities.length === 0
        })}">
          <button
            class="bdk-button"
            variant="outline"
            size="medium"
            full-width
            @click="${this.openCreateCollectionLayer}"
            data-test-ents-wishlist-collection-create-button
          >
            ${this.messages['ents.wishlist.collection.open.create.layer.button']}
          </button>
        </div>
        ${this.collectionsState.entities.length === 0 ? this.renderEmptyCollections() : this.renderCollections()}
      </div>
    `;
  }

  private renderEmptyCollections() {
    return html`
      <div class="ents-wishlist-collection-lists ents-wishlist-collection-lists__empty ents-wishlist-collection-grid">
        <div class="ents-wishlist-collection-lists__empty--inner">
          <h3 class="ents-copy-medium-bold" data-test-ents-wishlist-collection-empty-hint>
            ${this.messages['ents.wishlist.collections.empty.headline']}
          </h3>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents.wishlist.collections.empty.text1'])}
            </div>
            ${unsafeHTML(`<span>${this.messages['ents.wishlist.collections.empty.text1']}</span>`)}
          </div>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents.wishlist.collections.empty.text2'])}
            </div>
            ${this.messages['ents.wishlist.collections.empty.text2']}
          </div>
          <div class="ents-wishlist-collection-lists__empty--text ents-prose-copy-small">
            <div class="ents-wishlist-collection-lists__empty--icon">
              ${addTitleToIcon(checkmarkIcon(), this.messages['ents.wishlist.collections.empty.text3'])}
            </div>
            ${this.messages['ents.wishlist.collections.empty.text3']}
          </div>
        </div>
      </div>
    `;
  }

  private renderCollections() {
    const showShareIcon = this.collectionsState.entities.some(col => col.isSharingAllowed);
    return html`
      <div class="ents-wishlist-collection-lists ents-wishlist-collection-grid">
        ${repeat(this.collectionsState.entities, col => col.id, col => html`
          <article>
            <ents-wishlist-collection .wishlistCollection="${col}" ?show-share-icon="${showShareIcon}"/>
          </article>
        `)}
      </div>
    `;
  }
}
